import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, Download, Search, FolderOpen, Loader2,
  FolderUp, FolderPlus, ChevronRight, Folder
} from "lucide-react";
import BottomNav from "./BottomNav";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";

const ROOT_CATEGORIES = ["Ravijuhendid", "Protokollid", "Lepingud", "Juhendid", "Üldine"];

const authFetch = async (url, options = {}) => {
  const storedToken = localStorage.getItem("session_token");
  const headers = {
    ...options.headers,
    ...(storedToken ? { "Authorization": `Bearer ${storedToken}` } : {}),
  };
  return fetch(url, { ...options, headers });
};

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const API_URL = API;

  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);

  const pathParts = currentPath ? currentPath.split("/") : [];
  const isRoot = currentPath === "";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, foldersRes] = await Promise.all([
        authFetch(`${API_URL}/documents?folder=${encodeURIComponent(currentPath)}`),
        authFetch(`${API_URL}/documents/folders?parent=${encodeURIComponent(currentPath)}`),
      ]);
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (foldersRes.ok) setFolders(await foldersRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API_URL, currentPath]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navigateTo = (path) => {
    setCurrentPath(path);
    setSearchQuery("");
    setShowNewFolder(false);
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const res = await authFetch(`${API_URL}/documents/folders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, parent: currentPath }),
      });
      if (res.ok) {
        toast.success("Kaust loodud");
        setNewFolderName("");
        setShowNewFolder(false);
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Kausta loomine ebaõnnestus");
      }
    } catch (e) {
      toast.error("Kausta loomine ebaõnnestus");
    }
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm("Kas olete kindel, et soovite selle kausta kustutada?")) return;
    try {
      const res = await authFetch(`${API_URL}/documents/folders/${folderId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Kaust kustutatud");
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Kustutamine ebaõnnestus");
      }
    } catch (e) {
      toast.error("Kustutamine ebaõnnestus");
    }
  };

  const uploadSingleFile = async (file, folder) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", folder);
    formData.append("category", pathParts[0] || "Üldine");
    const res = await authFetch(`${API_URL}/documents/upload`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || "Üleslaadimine ebaõnnestus");
    }
    return res.json();
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Fail on liiga suur (max 25MB)");
      return;
    }
    setUploading(true);
    try {
      await uploadSingleFile(file, currentPath);
      toast.success("Fail üles laetud");
      fetchData();
    } catch (error) {
      toast.error(error.message || "Üleslaadimine ebaõnnestus");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleFolderUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const allowedExts = new Set(["pdf","doc","docx","xls","xlsx","ppt","pptx","txt","csv","jpg","jpeg","png","gif","zip","rtf"]);
    const validFiles = files.filter(f => {
      const ext = f.name.split(".").pop()?.toLowerCase();
      return ext && allowedExts.has(ext) && f.size <= 25 * 1024 * 1024;
    });

    if (validFiles.length === 0) {
      toast.error("Kaustas pole lubatud faile");
      e.target.value = "";
      return;
    }

    const skipped = files.length - validFiles.length;
    setUploading(true);
    setUploadProgress({ current: 0, total: validFiles.length });
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < validFiles.length; i++) {
      setUploadProgress({ current: i + 1, total: validFiles.length });
      try {
        await uploadSingleFile(validFiles[i], currentPath);
        successCount++;
      } catch { failCount++; }
    }

    setUploading(false);
    setUploadProgress(null);
    e.target.value = "";

    let msg = `${successCount}/${validFiles.length} faili üles laetud`;
    if (skipped > 0) msg += ` (${skipped} vahele jäetud)`;
    if (failCount > 0) toast.warning(msg);
    else toast.success(msg);
    fetchData();
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await authFetch(`${API_URL}/documents/download-token/${doc.doc_id}`, { method: "POST" });
      if (!res.ok) throw new Error("Token failed");
      const data = await res.json();
      window.open(`${API_URL}/documents/download/${doc.doc_id}?dl=${data.download_token}`, "_blank");
    } catch {
      // Fallback to legacy method
      const token = localStorage.getItem("session_token");
      window.open(`${API_URL}/documents/download/${doc.doc_id}?token=${token}`, "_blank");
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Kas olete kindel, et soovite selle faili kustutada?")) return;
    try {
      const res = await authFetch(`${API_URL}/documents/${docId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Fail kustutatud");
        setDocuments(prev => prev.filter(d => d.doc_id !== docId));
      }
    } catch (e) {
      toast.error("Kustutamine ebaõnnestus");
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split(".").pop()?.toLowerCase();
    if (["pdf"].includes(ext)) return "bg-red-100 text-red-600";
    if (["doc", "docx", "rtf", "txt"].includes(ext)) return "bg-blue-100 text-blue-600";
    if (["xls", "xlsx", "csv"].includes(ext)) return "bg-green-100 text-green-600";
    if (["jpg", "jpeg", "png", "gif"].includes(ext)) return "bg-purple-100 text-purple-600";
    return "bg-slate-100 text-slate-600";
  };

  const filtered = documents.filter(d => {
    if (!searchQuery) return true;
    return d.filename.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredFolders = isRoot
    ? ROOT_CATEGORIES.map(name => ({ folder_id: `root_${name}`, name, full_path: name, is_root: true }))
        .concat(folders.filter(f => !ROOT_CATEGORIES.includes(f.name)))
    : folders;

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="documents-page">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="md:ml-64 min-h-screen">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
            <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
            <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <FolderOpen className="w-5 h-5" /> MedDoc
            </h1>
          </div>
        </header>

        <main className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-sm flex-wrap" data-testid="breadcrumb">
            <button
              onClick={() => navigateTo("")}
              className={`font-medium transition-colors ${isRoot ? "text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
              data-testid="breadcrumb-root"
            >
              MedDoc
            </button>
            {pathParts.map((part, i) => {
              const path = pathParts.slice(0, i + 1).join("/");
              const isLast = i === pathParts.length - 1;
              return (
                <span key={path} className="flex items-center gap-1.5">
                  <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                  <button
                    onClick={() => navigateTo(path)}
                    className={`font-medium transition-colors ${isLast ? "text-slate-900" : "text-slate-500 hover:text-slate-800"}`}
                    data-testid={`breadcrumb-${i}`}
                  >
                    {part}
                  </button>
                </span>
              );
            })}
          </div>

          {/* Actions bar */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Otsi failinime järgi..."
                className="pl-9"
                data-testid="doc-search-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="border-slate-300"
                onClick={() => setShowNewFolder(!showNewFolder)}
                data-testid="new-folder-toggle"
              >
                <FolderPlus className="w-4 h-4 mr-1" /> Kaust
              </Button>
              <label className="cursor-pointer" data-testid="doc-upload-btn">
                <input type="file" className="hidden" onChange={handleUpload} disabled={uploading || isRoot} />
                <Button asChild disabled={uploading || isRoot} className="bg-slate-900 hover:bg-slate-800">
                  <span><Upload className="w-4 h-4 mr-2" /> Fail</span>
                </Button>
              </label>
              <label className="cursor-pointer" data-testid="doc-folder-upload-btn">
                <input type="file" className="hidden" onChange={handleFolderUpload} disabled={uploading || isRoot} webkitdirectory="" directory="" multiple />
                <Button asChild disabled={uploading || isRoot} variant="outline" className="border-slate-300">
                  <span><FolderUp className="w-4 h-4 mr-2" /> Failid</span>
                </Button>
              </label>
            </div>
          </div>

          {/* New folder input */}
          {showNewFolder && (
            <div className="flex gap-2" data-testid="new-folder-form">
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Uue kausta nimi..."
                className="flex-1"
                onKeyDown={(e) => e.key === "Enter" && createFolder()}
                autoFocus
                data-testid="new-folder-input"
              />
              <Button onClick={createFolder} className="bg-slate-900 hover:bg-slate-800" data-testid="create-folder-btn">
                Loo
              </Button>
            </div>
          )}

          {/* Upload progress */}
          {uploadProgress && (
            <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3" data-testid="upload-progress">
              <Loader2 className="w-4 h-4 animate-spin text-slate-500 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-700 font-medium">Laen üles...</span>
                  <span className="text-slate-500">{uploadProgress.current}/{uploadProgress.total}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5">
                  <div className="bg-slate-900 h-1.5 rounded-full transition-all duration-300" style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }} />
                </div>
              </div>
            </div>
          )}

          {isRoot && (
            <p className="text-xs text-slate-400">Valige kaust, kuhu failid üles laadida</p>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Folders */}
              {filteredFolders.length > 0 && !searchQuery && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="folders-grid">
                  {filteredFolders.map(folder => (
                    <Card
                      key={folder.folder_id}
                      className="border-slate-200 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all group"
                      onClick={() => navigateTo(folder.full_path)}
                      data-testid={`folder-${folder.folder_id}`}
                    >
                      <CardContent className="p-3 flex items-center gap-3">
                        <Folder className="w-8 h-8 text-amber-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-slate-800 truncate flex-1">{folder.name}</span>
                        {!folder.is_root && (
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteFolder(folder.folder_id); }}
                            className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                            data-testid={`delete-folder-${folder.folder_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Files */}
              {filtered.length === 0 && !isRoot ? (
                <Card className="border-slate-200">
                  <CardContent className="py-8 text-center text-slate-400">
                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>{searchQuery ? "Otsingule ei vasta ükski fail" : "Kaustas pole faile"}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {filtered.map(doc => (
                    <Card key={doc.doc_id} className="border-slate-200 group hover:shadow-md transition-shadow" data-testid={`doc-card-${doc.doc_id}`}>
                      <CardContent className="p-3 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getFileIcon(doc.filename)}`}>
                          <FileText className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">{doc.filename}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <span>{formatFileSize(doc.size)}</span>
                            <span className="hidden sm:inline">{doc.uploaded_by_name}</span>
                            <span>{new Date(doc.uploaded_at).toLocaleDateString("et-EE")}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => downloadDoc(doc)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700" data-testid={`download-${doc.doc_id}`}>
                            <Download className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteDoc(doc.doc_id)} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-doc-${doc.doc_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>
      <BottomNav />
    </div>
  );
}
